import {useAuth} from "../../../hooks/useAuth";
import React, {useEffect, useRef, useState} from "react";
import Pusher from "pusher-js";
import {APIPostNotificationsUnread} from "../api/notifications";
import CustomButton from "../../../shared/ui/CustomButton";
import Icon from "../../../shared/ui/Icon";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";

type ExportMessage = {
    title: string;
    message: string;
    filePath?: string;
};

const PusherNotifications = () => {
    const {tenant, user, update} = useAuth();
    const [exportMessages, setExportMessages] = useState<ExportMessage[]>([]);
    const receivedExportFiles = useRef<string[]>([]);

    const currentExportMessage = exportMessages[0];

    const closeExportMessage = () => {
        setExportMessages((prevMessages) => prevMessages.slice(1));
    };

    const downloadFile = (filePath: string) => {
        const link = document.createElement("a");
        link.href = filePath;
        link.download = filePath.split("/").pop() || "export.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY || "", {
            cluster: "eu",
        });
        const channelName = `channel_${tenant.id}`;
        const eventName = `event_${tenant.id}_test_notification`;
        const channel1 = pusher.subscribe(channelName);
        // You can bind more channels here like this
        // const channel2 = pusher.subscribe('channel_name2')

        const notificationHandler = function (data: any) {
            if (data.data?.action === "export" && user.id === data.data.user_id) {
                const filePath = data.data.file_path;

                if (filePath && receivedExportFiles.current.includes(filePath)) {
                    return;
                }

                if (filePath) {
                    receivedExportFiles.current.push(filePath);
                }

                setExportMessages((prevMessages) => [
                    ...prevMessages,
                    {
                        title: "Exportul a fost finalizat cu succes",
                        message: "Apasă pe butonul de mai jos pentru descărcarea tabelului de rezultate.",
                        filePath,
                    },
                ]);
            }

            APIPostNotificationsUnread()
                .then((response) => {
                    if (response.data.success) {
                        update("user", {notifications: response.data.data.value});
                    } else {
                        update("error", {show: true, code: "A002"});
                    }
                })
                .catch((err) => {
                    update("error", {show: true, code: "A001"});
                });
        };

        channel1.bind(eventName, notificationHandler);

        return () => {
            channel1.unbind(eventName, notificationHandler);
            pusher.unsubscribe(channelName);
        };
    }, []);

    return (
        <Dialog
            open={Boolean(currentExportMessage)}
            onClose={(event, reason) => {
                if (reason === "backdropClick" || reason === "escapeKeyDown") {
                    return;
                }

                closeExportMessage();
            }}
            disableEscapeKeyDown
            hideBackdrop
            maxWidth={false}
            PaperProps={{
                sx: (theme) => ({
                    position: "fixed",
                    top: theme.spacing(3),
                    right: theme.spacing(3),
                    m: 0,
                    width: {
                        xs: "calc(100% - 32px)",
                        sm: 420,
                    },
                    maxWidth: "calc(100% - 32px)",
                    backgroundColor: "background.paper",
                    backgroundImage: "none",
                    borderRadius: 3,
                    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.18)",
                    overflow: "hidden",
                }),
            }}
            sx={{
                pointerEvents: "none",
                "& .MuiDialog-container": {
                    alignItems: "flex-start",
                    justifyContent: "flex-end",
                },
                "& .MuiPaper-root": {
                    pointerEvents: "auto",
                },
            }}
        >
            <Box
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    p: theme.spacing(2),
                })}
            >
                <Box
                    sx={(theme) => ({
                        width: 40,
                        height: 40,
                        minWidth: 40,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "success.light",
                        color: "success.contrastText",
                        mt: 0.25,
                    })}
                >
                    <Icon icon="far arrow-down-to-line" fixedWidth={true}/>
                </Box>

                <Box flexGrow={1} minWidth={0}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                        <Typography variant="h6" sx={{fontWeight: 600, lineHeight: 1.25}}>
                            {currentExportMessage?.title}
                        </Typography>

                        <IconButton
                            onClick={closeExportMessage}
                            aria-label="close"
                            size="small"
                            sx={{mt: -0.5, mr: -0.5}}
                        >
                            <Icon icon="fal xmark" fixedWidth={true}/>
                        </IconButton>
                    </Box>

                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {currentExportMessage?.message}
                    </Typography>

                    {currentExportMessage?.filePath && (
                        <Box mt={1.5}>
                            <CustomButton
                                style={{fontWeight: 500}}
                                buttonType="textIcon"
                                bgcolor="dark"
                                onClick={() => downloadFile(currentExportMessage.filePath || "")}
                                icon={<Icon icon="far arrow-down-to-line" fixedWidth={true}/>}
                            >
                                Descarcă fișierul
                            </CustomButton>
                        </Box>
                    )}
                </Box>
            </Box>
        </Dialog>
    );


}

export default PusherNotifications;
